import React, {useCallback, useEffect, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import Spinner from '../components/Spinner';
import {LuggageIcon, PlusIcon} from '../components/icons';
import {useDocumentTitle} from '../hooks/useDocumentTitle';
import {
    fetchTrips,
    formatTripDates,
    TripListItem,
    TripStatus,
    TRIP_STATUS_LABELS,
} from '../service/TripApi';

/** Фильтры списка: «Все» + каждый статус жизненного цикла. */
const STATUS_FILTERS: Array<{value: TripStatus | null; label: string}> = [
    {value: null, label: 'Все'},
    {value: 'PLANNING', label: TRIP_STATUS_LABELS.PLANNING},
    {value: 'PACKING', label: TRIP_STATUS_LABELS.PACKING},
    {value: 'IN_PROGRESS', label: TRIP_STATUS_LABELS.IN_PROGRESS},
    {value: 'COMPLETED', label: TRIP_STATUS_LABELS.COMPLETED},
    {value: 'CANCELLED', label: TRIP_STATUS_LABELS.CANCELLED},
];

/** Цвет статус-чипа: статус читается и без текста, но текст всегда рядом (доступность). */
const STATUS_CHIP: Record<TripStatus, string> = {
    PLANNING: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    PACKING: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    IN_PROGRESS: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    COMPLETED: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    CANCELLED: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};

export const tripDestination = (trip: TripListItem): string =>
    [trip.city, trip.country].filter(Boolean).join(', ');

const TripListPage: React.FC = () => {
    const navigate = useNavigate();
    const [trips, setTrips] = useState<TripListItem[]>([]);
    const [status, setStatus] = useState<TripStatus | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useDocumentTitle();

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setTrips(await fetchTrips(status));
        } catch (e) {
            setError('Не удалось загрузить поездки. Попробуйте ещё раз.');
        } finally {
            setLoading(false);
        }
    }, [status]);

    useEffect(() => {
        load();
    }, [load]);

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Поездки</h1>
                <button
                    onClick={() => navigate('/trips/new')}
                    data-testid="trip-create-open"
                    className="btn-primary flex items-center gap-2 min-h-[44px]"
                >
                    <PlusIcon className="w-5 h-5"/>
                    Создать поездку
                </button>
            </div>

            <div className="flex flex-wrap gap-2" role="group" aria-label="Фильтр по статусу">
                {STATUS_FILTERS.map(filter => (
                    <button
                        key={filter.value ?? 'all'}
                        onClick={() => setStatus(filter.value)}
                        data-testid={`trip-filter-${filter.value ?? 'all'}`}
                        aria-pressed={status === filter.value}
                        className={`px-3 py-2 rounded-lg text-sm min-h-[44px] transition-colors ${
                            status === filter.value
                                ? 'bg-brand-500 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
                        }`}
                    >
                        {filter.label}
                    </button>
                ))}
            </div>

            {error && (
                <div
                    data-testid="trip-list-error"
                    role="alert"
                    className="p-4 rounded-lg border border-red-300 bg-red-50 text-red-700 dark:border-red-700 dark:bg-red-900/30 dark:text-red-300 flex items-center justify-between gap-4"
                >
                    <span>{error}</span>
                    <button onClick={load} className="underline min-h-[44px]">Повторить</button>
                </div>
            )}

            {loading && (
                <div className="flex justify-center py-12" data-testid="trip-list-loading">
                    <Spinner/>
                </div>
            )}

            {!loading && !error && trips.length === 0 && (
                <div className="text-center py-12 space-y-4" data-testid="trip-list-empty">
                    <LuggageIcon className="w-10 h-10 mx-auto text-gray-400"/>
                    <p className="text-gray-500 dark:text-gray-400">
                        Поездок пока нет. Заведите первую — система соберёт образы по дням и список вещей.
                    </p>
                    <button onClick={() => navigate('/trips/new')} className="btn-primary min-h-[44px]"
                            data-testid="trip-empty-cta">
                        Создать поездку
                    </button>
                </div>
            )}

            {!loading && trips.length > 0 && (
                <ul className="grid gap-3" data-testid="trip-list">
                    {trips.map(trip => (
                        <li key={trip.id}>
                            <Link
                                to={`/trips/${trip.id}`}
                                data-testid={`trip-card-${trip.id}`}
                                className="block p-4 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-500 transition-colors"
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <div className="min-w-0">
                                        <h2 className="font-medium text-gray-900 dark:text-gray-100 truncate">{trip.name}</h2>
                                        <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                            {tripDestination(trip) || 'Направление не указано'}
                                        </p>
                                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                            {formatTripDates(trip.startDate, trip.endDate)} · {trip.dayCount} дн.
                                        </p>
                                    </div>
                                    <span className={`px-2 py-1 rounded-md text-xs whitespace-nowrap ${STATUS_CHIP[trip.status]}`}>
                                        {TRIP_STATUS_LABELS[trip.status]}
                                    </span>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                                    Вещи: {trip.itemsPacked} / {trip.itemsTotal} · Чек-лист: {trip.checklistDone} / {trip.checklistTotal}
                                </p>
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default TripListPage;
